import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { MateriaPublicadaHtmlHierarquiaOutputDto } from 'src/app/domain/DTOs/output/publicacao/materia-publicada-html-hierarquia-output.dto';
import { CadernoEdicaoPublicadoOutputDto, EdicaoPublicadaPortalOutputDto } from 'src/app/domain/DTOs/output/publicacao/edicao-publicada-portal-output.dto';
import { baixarPdfCadernoEdicao, baixarPdfCadernoEdicaoFinish, selectCarregandoBaixarPdfCadernoEdicao } from 'src/app/stateManagement/full-states/publicacao/baixar-pdf-caderno-edicao.state';
import { baixarPdfMateriaPublicada, baixarPdfMateriaPublicadaFinish, selectPublicacaoIdEmDownload } from 'src/app/stateManagement/full-states/publicacao/baixar-pdf-materia-publicada.state';

@Component({
  selector: 'app-materias-html',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './materias-html.component.html',
  styleUrl: './materias-html.component.css'
})
export class MateriasHtmlComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly LadosBorda = ['top', 'right', 'bottom', 'left'] as const;
  private static readonly QuantidadeColunasTabelaLarga = 6;
  private static readonly QuantidadeColunasTabelaMuitoLarga = 9;
  private static readonly QuantidadeColunasTabelaExtremamenteLarga = 12;
  private static readonly ZoomMinimo = 0.25;
  private static readonly ZoomMaximo = 1.75;
  private static readonly PassoZoom = 0.25;
  private static readonly EspacamentoAcessibilidade = 24;

  @ViewChild('controlesZoom', { read: ElementRef }) controlesZoom?: ElementRef<HTMLElement>;

  @Input({ required: true }) edicao!: EdicaoPublicadaPortalOutputDto;
  @Input({ required: true }) caderno!: CadernoEdicaoPublicadoOutputDto;
  @Input() set materias(materias: MateriaPublicadaHtmlHierarquiaOutputDto[]) {
    this._materias = (materias ?? []).map(materia => ({
      ...materia,
      conteudo: this.prepararConteudo(materia.conteudo)
    }));
  }

  public get materias(): MateriaPublicadaHtmlHierarquiaOutputDto[] {
    return this._materias;
  }

  @Input() carregando = false;
  @Input() erro = '';
  @Input() possuiProximaPagina = false;
  @Output() carregarMais = new EventEmitter<void>();

  readonly diarioExecutivo = 1;
  readonly diarioMunicipios = 2;
  readonly diarioTerceiros = 3;

  public baixandoCadernoEdicao = false;
  public publicacaoIdEmDownload: number | null = null;
  public nivelZoom = 1;
  private _materias: MateriaPublicadaHtmlHierarquiaOutputDto[] = [];
  private readonly subscriptions = new Subscription();
  private framePosicionamentoAcessibilidade?: number;
  private observadorAcessibilidade?: MutationObserver;
  private elementoAcessibilidade?: HTMLElement;

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.select(selectCarregandoBaixarPdfCadernoEdicao).subscribe(carregando => this.baixandoCadernoEdicao = carregando)
    );
    this.subscriptions.add(
      this.store.select(selectPublicacaoIdEmDownload).subscribe(
        publicacaoId => this.publicacaoIdEmDownload = publicacaoId
      )
    );
  }

  ngAfterViewInit(): void {
    this.observarEstadoAcessibilidade();
    this.agendarPosicionamentoAcessibilidade();
  }

  ngOnDestroy(): void {
    if (this.framePosicionamentoAcessibilidade !== undefined)
      cancelAnimationFrame(this.framePosicionamentoAcessibilidade);

    this.observadorAcessibilidade?.disconnect();
    this.restaurarPosicionamentoAcessibilidade();
    this.subscriptions.unsubscribe();
    this.store.dispatch(baixarPdfCadernoEdicaoFinish());
    this.store.dispatch(baixarPdfMateriaPublicadaFinish());
  }

  public formatarData(data: Date): string {
    const valor = new Date(data);
    const texto = new Intl.DateTimeFormat('pt-BR', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
    }).format(valor);
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }

  public voltarAoTopo(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public baixarPublicacao(publicacaoId: number): void {
    if (this.publicacaoIdEmDownload !== null)
      return;

    this.store.dispatch(baixarPdfMateriaPublicada({ publicacaoId }));
  }

  public baixarCadernoEdicaoCompleto(): void {
    if (this.baixandoCadernoEdicao) 
      return;

    this.store.dispatch(baixarPdfCadernoEdicao({ cadernoEdicaoId: this.caderno.id }));
  }

  public get podeAumentarZoom(): boolean {
    return this.nivelZoom < MateriasHtmlComponent.ZoomMaximo;
  }

  public get podeDiminuirZoom(): boolean {
    return this.nivelZoom > MateriasHtmlComponent.ZoomMinimo;
  }

  public get percentualZoom(): number {
    return Math.round(this.nivelZoom * 100);
  }

  public aumentarZoom(): void {
    this.alterarZoom(MateriasHtmlComponent.PassoZoom);
  }

  public diminuirZoom(): void {
    this.alterarZoom(-MateriasHtmlComponent.PassoZoom);
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  public aoAlterarViewport(): void {
    this.agendarPosicionamentoAcessibilidade();
  }

  private prepararConteudo(conteudo: string): string {
    if (!conteudo)
      return '';

    const container = document.createElement('div');
    container.innerHTML = conteudo;

    container.querySelectorAll<HTMLTableElement>('table').forEach(tabela => {
      this.preservarBordasOriginais(tabela);
      this.classificarTabelaPorQuantidadeColunas(tabela);
      this.envolverTabelaEmContainer(tabela);
    });

    return container.innerHTML;
  }

  private preservarBordasOriginais(tabela: HTMLTableElement): void {
    const elementos = [tabela, ...Array.from(tabela.querySelectorAll<HTMLElement>('th, td'))];

    elementos.forEach(elemento => {
      MateriasHtmlComponent.LadosBorda.forEach(lado => {
        if (this.possuiBordaInlineVisivel(elemento, lado))
          elemento.classList.add(`conteudo-borda-${lado}`);
      });
    });

    const atributoBorda = Number.parseFloat(tabela.getAttribute('border') ?? '0');
    if (!Number.isFinite(atributoBorda) || atributoBorda <= 0)
      return;

    tabela.querySelectorAll<HTMLElement>('th, td').forEach(celula => {
      MateriasHtmlComponent.LadosBorda.forEach(lado => celula.classList.add(`conteudo-borda-${lado}`));
    });
  }

  private possuiBordaInlineVisivel(elemento: HTMLElement, lado: typeof MateriasHtmlComponent.LadosBorda[number]): boolean {
    const estilo = elemento.style.getPropertyValue(`border-${lado}-style`).trim().toLowerCase();
    const largura = elemento.style.getPropertyValue(`border-${lado}-width`).trim().toLowerCase();

    return estilo !== ''
      && estilo !== 'none'
      && estilo !== 'hidden'
      && largura !== '0'
      && largura !== '0px';
  }

  private classificarTabelaPorQuantidadeColunas(tabela: HTMLTableElement): void {
    const quantidadeColunas = Array.from(tabela.rows).reduce((maiorQuantidade, linha) => {
      const quantidadeLinha = Array.from(linha.cells)
        .reduce((total, celula) => total + Math.max(celula.colSpan, 1), 0);

      return Math.max(maiorQuantidade, quantidadeLinha);
    }, 0);

    if (quantidadeColunas >= MateriasHtmlComponent.QuantidadeColunasTabelaExtremamenteLarga)
      tabela.classList.add('tabela-extremamente-larga');
    else if (quantidadeColunas >= MateriasHtmlComponent.QuantidadeColunasTabelaMuitoLarga)
      tabela.classList.add('tabela-muito-larga');
    else if (quantidadeColunas >= MateriasHtmlComponent.QuantidadeColunasTabelaLarga)
      tabela.classList.add('tabela-larga');
  }

  private envolverTabelaEmContainer(tabela: HTMLTableElement): void {
    if (tabela.parentElement?.classList.contains('tabela-container'))
      return;

    const wrapper = document.createElement('div');
    wrapper.className = 'tabela-container';
    tabela.parentNode?.insertBefore(wrapper, tabela);
    wrapper.appendChild(tabela);
  }

  private alterarZoom(passo: number): void {
    const novoZoom = Math.min(
      MateriasHtmlComponent.ZoomMaximo,
      Math.max(MateriasHtmlComponent.ZoomMinimo, this.nivelZoom + passo)
    );

    this.nivelZoom = Math.round(novoZoom * 100) / 100;
  }

  private agendarPosicionamentoAcessibilidade(): void {
    if (this.framePosicionamentoAcessibilidade !== undefined)
      cancelAnimationFrame(this.framePosicionamentoAcessibilidade);

    this.framePosicionamentoAcessibilidade = requestAnimationFrame(() => {
      this.framePosicionamentoAcessibilidade = undefined;
      this.posicionarAcessibilidadeAbaixoDoZoom();
    });
  }

  private observarEstadoAcessibilidade(): void {
    const acessibilidade = document.querySelector<HTMLElement>('[vw]');
    const painel = acessibilidade?.querySelector<HTMLElement>('[vw-plugin-wrapper]');
    if (!acessibilidade || !painel)
      return;

    this.elementoAcessibilidade = acessibilidade;
    this.observadorAcessibilidade = new MutationObserver(() => this.agendarPosicionamentoAcessibilidade());
    this.observadorAcessibilidade.observe(painel, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private posicionarAcessibilidadeAbaixoDoZoom(): void {
    const acessibilidade = document.querySelector<HTMLElement>('[vw]');
    const controlesZoom = this.controlesZoom?.nativeElement;
    if (!acessibilidade || !controlesZoom)
      return;

    this.elementoAcessibilidade = acessibilidade;
    this.limparPosicionamentoAcessibilidade(acessibilidade);

    const painelAberto = acessibilidade.querySelector<HTMLElement>('[vw-plugin-wrapper].active');
    if (painelAberto) {
      this.posicionarAcessibilidadeAbertaAbaixoDoZoom(acessibilidade, controlesZoom, painelAberto);
      return;
    }

    const limiteInferiorZoom = this.obterLimiteInferiorZoom(controlesZoom);
    const topo = limiteInferiorZoom + MateriasHtmlComponent.EspacamentoAcessibilidade;

    acessibilidade.style.setProperty('--vlibras-jornal-topo', `${topo}px`);
    acessibilidade.classList.add('vlibras-jornal--fechado');
  }

  private posicionarAcessibilidadeAbertaAbaixoDoZoom(
    acessibilidade: HTMLElement,
    controlesZoom: HTMLElement,
    painelAberto: HTMLElement
  ): void {
    const limitesPainel = painelAberto.getBoundingClientRect();
    const topo = this.obterLimiteInferiorZoom(controlesZoom)
      + MateriasHtmlComponent.EspacamentoAcessibilidade;
    const deslocamentoPainel = topo - limitesPainel.top;

    acessibilidade.style.setProperty('--vlibras-jornal-deslocamento', `${deslocamentoPainel}px`);
    acessibilidade.classList.add('vlibras-jornal--aberto');
  }

  private obterLimiteInferiorZoom(controlesZoom: HTMLElement): number {
    return Array.from(
      controlesZoom.querySelectorAll<HTMLElement>('.jornal-zoom-flutuante__botao')
    ).reduce(
      (maiorLimite, botao) => Math.max(maiorLimite, botao.getBoundingClientRect().bottom),
      controlesZoom.getBoundingClientRect().bottom
    );
  }

  private restaurarPosicionamentoAcessibilidade(): void {
    if (this.elementoAcessibilidade)
      this.limparPosicionamentoAcessibilidade(this.elementoAcessibilidade);
  }

  private limparPosicionamentoAcessibilidade(acessibilidade: HTMLElement): void {
    acessibilidade.classList.remove('vlibras-jornal--aberto', 'vlibras-jornal--fechado');
    acessibilidade.style.removeProperty('--vlibras-jornal-topo');
    acessibilidade.style.removeProperty('--vlibras-jornal-deslocamento');
  }

}
